import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {
    MvsApiCrudServiceCommentsInterface
} from '../../../../core/shared/service/api/mvs-api-crud-service-comments.interface';

/**
 * AI:
 * Status: 'implemented'
 * Type: Service
 * SubType: CRUD Entity Service
 * Reason: CRUD for {@code dq.DqAuditFindingComment} over {@code /mvsa/dq/auditFindingComments}.
 *
 * TM-1000002006: this service now satisfies {@link MvsApiCrudServiceCommentsInterface} so the SHARED
 * {@code <mvs-object-comments>} accepts it. The workbench dropped its hand-rolled comment pane in
 * favour of that component, which is what gives dq the same edit / delete / pin / copy / @mention
 * options cm and cr already have. The component resolves this service through the entity registry
 * ({@code dq.entity-provider.ts}) and rejects any service that does not carry
 * {@code getCommentAttribute} — see {@code MvsObjectCommentsComponent.checkLinkedEntityService}.
 *
 * <h3>WRITING NOW GOES THROUGH THE GENERIC ENDPOINT — a deliberate change</h3>
 * Until TM-1000002006 a new comment was written by {@code DqAuditActionService.addComment} against
 * {@code POST /mvsa/dq/auditFindings/{id}/comments}, because only there is the finding resolved and
 * the write permission checked ON IT. The shared component creates through
 * {@code MvsCrudOneToManyComponent.actionCreateLinkedEntity}, i.e. through {@code POST} on this
 * service, setting {@code findingDtoId} from {@link getLinkedAttribute} + {@code "DtoId"}. Editing,
 * pinning and the soft delete run over {@code PUT} here as well. That is the price of the shared
 * component and it was accepted with the ticket; the finding-scoped endpoint is untouched and is
 * still what a decision (Quittierung / Fehlalarm) uses for its comment.
 *
 * The route segment is a STRING LITERAL from the backend controller's {@code @RequestMapping} and is
 * derived from nothing — neither from the entity name nor from the alias. It is written out
 * verbatim here and must NEVER be assembled.
 *
 * @ticket TM-1000002006
 */
@Injectable({providedIn: 'root'})
export class DqAuditFindingCommentService extends MvsCrudService
    implements MvsApiCrudServiceCommentsInterface {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/dq/auditFindingComments');
    }

    override getObjectIcon(): string {
        return 'fa-solid fa-comment';
    }

    override getObjectLabels(): string[] {
        return ['Befund-Kommentar', 'Befund-Kommentare'];
    }

    /**
     * The attribute that carries the comment text. Read AND written by the shared component — it
     * builds the create DTO as {@code dto[getCommentAttribute()] = text}.
     *
     * @ticket TM-1000002006
     */
    getCommentAttribute(): string {
        return 'comment';
    }

    /**
     * Pinning is offered.
     *
     * Backed by the entity: {@code DqAuditFindingComment implements EntityCommentPinned} and carries
     * a {@code Boolean pinned} column (added under this same ticket, mirroring
     * {@code cm.ContractComment} / {@code cr.CustomerComment}). Returning {@code true} also makes the
     * shared component sort pinned comments to the top before applying the selected order.
     *
     * @ticket TM-1000002006
     */
    hasCommentPinOption(): boolean {
        return true;
    }

    /**
     * The attribute on {@code dq.DqAuditFindingComment} that points at the given main object.
     *
     * <h4>THE RETURN VALUE IS THE BARE RELATION NAME. NOT A PATH, NOT AN ID FIELD.</h4>
     * {@code "finding"} — never {@code "finding.id"}, {@code "findingId"} or {@code "findingDtoId"}.
     * The resolver dereferences the association itself and the id goes in the filter VALUE. Do not
     * reason from the entity having a {@code DqAuditFinding finding} field that a dotted path must
     * work: it does not, and that inference has produced the wrong spelling twice in this module
     * already.
     *
     * <p>Measured against dev/tenantA, finding 25993, identical request bodies otherwise:</p>
     * <pre>
     *   finding        -> HTTP 200, entries=6   &lt;- the ONLY spelling that resolves
     *   finding.id     -> HTTP 200, entries=0, errorMessage "Could not resolve attribute 'finding.id'"
     *   findingId      -> HTTP 200, entries=0, same shape
     *   findingDtoId   -> HTTP 200, entries=0, same shape
     * </pre>
     *
     * <p>WHY A WRONG VALUE LOOKS LIKE NOTHING IS WRONG. The endpoint answers HTTP <b>200</b> with
     * {@code {"entries": [], "errorMessage": "Could not resolve attribute ..."}} — a success status
     * carrying the error in the BODY. No {@code error} handler anywhere fires, the list is empty, and
     * the pane renders „Noch keine Kommentare". A list that CANNOT BE READ is indistinguishable from
     * one that is genuinely empty. That is exactly how the dq comment history stayed invisible while
     * comments were being stored correctly all along.</p>
     *
     * <p>Reference implementations returning the bare name: {@code cm/.../contract-comment.service.ts}
     * ({@code "contract"}) and {@code cr/.../customer-comment.service.ts} ({@code "customer"}).</p>
     *
     * @param objectType alias of the main object the comments hang on
     * @ticket TM-1000002006
     */
    override getLinkedAttribute(objectType: string): string {

        if (objectType === 'dq.DqAuditFinding') {
            return 'finding';
        }

        return super.getLinkedAttribute(objectType);
    }
}
